<template>
  <user-login-wrapper>
    <el-card class="full-card" shadow="always">
      <template #header>
        <div v-if="proximityInfo != null">
          <jr-applicant-title
            titleName="回答案作成"
            :applicantNumber="proximityInfo.applicant_number"
            :applicantTitle="proximityInfo.applicant_title"
          />
        </div>
      </template>
      <template #default>
        <double-scroll>
          <template #left>
            <proximity-info
              v-if="requestHeaderId"
              :isEdit="false"
              :requestHeaderId="requestHeaderId"
              @update:form="proximityInfo = $event"
              :returnToList="false"
              :isActionBtnVisible="false"
              :isHistoryTableVisible="true"
            />
          </template>
          <template #right>
            <!-- フッター -->
            <div class="footer-wrapper" v-if="requestHeaderId">
              <div class="footer double-scroll-padding">
                <el-row>
                  <el-col :span="12" class="actions">
                    <jr-button type="success" size="small" @click="copy" class="copy-btn">回答項目を複写</jr-button>
                    <jr-button type="success" size="small" @click="paste" class="paste-btn"
                      >回答項目を貼り付け</jr-button
                    >
                  </el-col>
                  <el-col :span="12" class="actions align-right export-btn" v-if="proximityInfo != null">
                    <answer-print-export
                      :formRef="formRef"
                      :answerDraftItems="answerDraftItem"
                      :proximity="proximityInfo"
                      :answerApplicantAbbreviation="form.answer_applicant_abbreviation"
                      :answerDraftTitle="form.answer_draft_title"
                    />
                  </el-col>
                </el-row>
              </div>
            </div>

            <div class="right-panels double-scroll-padding" v-if="proximityInfo != null">
              <!-- 回答案作成 -->
              <input-group-dark class="word-preview-area" title="回答案作成">
                <el-form :model="form" ref="formRef" :rules="answerRule">
                  <el-descriptions :column="2" border size="small">
                    <el-descriptions-item label="回答案件名">
                      <el-form-item prop="answer_draft_title" class="answer_draft_title">
                        <jr-text v-model="form.answer_draft_title" :placeholder="proximityInfo.applicant_title" />
                      </el-form-item>
                    </el-descriptions-item>
                  </el-descriptions>
                  <el-descriptions :column="2" border size="small">
                    <el-descriptions-item label="相手敬称">
                      <el-form-item prop="answer_applicant_abbreviation" class="answer_applicant_abbreviation">
                        <jr-text v-model="form.answer_applicant_abbreviation" />
                      </el-form-item>
                    </el-descriptions-item>
                  </el-descriptions>
                  <!-- 回答案候補 -->
                  <div class="my">
                    <label class="text-sm"> 回答候補 </label>
                    <answer-draft-check-box
                      v-if="proximityInfo != null"
                      :requestHeaderId="requestHeaderId"
                      :modelValue="storeAnswerItems"
                      :proximity="proximityInfo"
                      @update:modelValue="updateAnswerItem($event)"
                    />
                  </div>
                </el-form>
                <!-- Wordプレビュー表示 -->
                <Suspense>
                  <answer-preview
                    :items="answerDraftItem"
                    :proximity="proximityInfo"
                    :answerApplicantAbbreviation="form.answer_applicant_abbreviation"
                    :answerDraftTitle="form.answer_draft_title"
                  />
                </Suspense>
              </input-group-dark>
            </div>
          </template>
        </double-scroll>
      </template>
    </el-card>
  </user-login-wrapper>
</template>

<script lang="ts">
import UserLoginWrapper from '@/components/auth-wrappers/UserLoginWrapper.vue'
import InputGroupDark from '@/components/layout/InputGroupDark.vue'
import DoubleScroll from '@/components/layout/DoubleScroll.vue'
import ProximityInfo from '@/components/layout/ProximityInfo.vue'
import AnswerDraftCheckBox from '@/components/inputs/AnswerDraftCheckBox.vue'
import AnswerPreview from '@/components/layout/AnswerPreview.vue'
import JrButton from '@/components/jr-parts/JrButton.vue'
import JrText from '@/components/jr-parts/JrText.vue'
import JrApplicantTitle from '@/components/jr-parts/JrApplicantTitle.vue'
import AnswerPrintExport from '@/components/actions/AnswerPrintExport.vue'
import { answerRule } from '@/constants/rules'

import { defineComponent, computed, ref, watch, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { RequestHeaderProximityForm } from '@/types/forms'
import Logger from '@/services/logger'
import { ElForm } from 'element-plus'
import { addMessageQueue, MESSAGE_TYPE_TOAST, MESSAGE_TYPE_ERROR } from '@/plugins/messageQueue'
import Message from '@/services/message'
import { useStore, Store } from '@/store'

interface AnswerForm {
  answer_draft_title: string | undefined | null
  answer_applicant_abbreviation: string | null
}

/**
 * 回答案作成
 */
export default defineComponent({
  name: 'ProximityAnswer',
  components: {
    InputGroupDark,
    DoubleScroll,
    UserLoginWrapper,
    ProximityInfo,
    AnswerDraftCheckBox,
    AnswerPreview,
    JrButton,
    JrText,
    JrApplicantTitle,
    AnswerPrintExport,
  },

  setup() {
    const store = useStore() as Store
    const route = useRoute()
    const router = useRouter()
    const formRef = ref<InstanceType<typeof ElForm>>()
    const storeAnswerItems = reactive<number[]>([])
    const requestHeaderId = computed(() => {
      return Number(route.params.request_header_id)
    })

    // 申請基本IDが正しくない場合404へ飛ばす
    if (Number.isNaN(requestHeaderId.value)) {
      Logger.info('申請基本IDが正しくない')
      router.push({ name: 'NotFound' })
    }

    const proximityInfo = ref<RequestHeaderProximityForm | null>(null)
    const form = reactive<AnswerForm>({
      answer_draft_title: '',
      answer_applicant_abbreviation: '',
    })
    const answerDraftItem = reactive<number[]>([])

    /**
     * 回答案候補でクリックされている全て回答項目情報を取得
     */
    const updateAnswerItem = (answerItem: number[]) => {
      answerDraftItem.splice(0)
      answerDraftItem.push(...answerItem)
    }

    /**
     * 回答項目複写
     */
    const copy = () => {
      if (answerDraftItem.length === 0) {
        // 対象が0件の場合はエラーメッセージを表示
        addMessageQueue(MESSAGE_TYPE_ERROR, Message.codes.NONE_COPY_TARGET)
        return
      }

      //storeに登録
      //別の配列に格納
      const tmpArray: number[] = [...answerDraftItem]
      store.commit('user/setAnswerDraftItems', tmpArray)
      addMessageQueue(MESSAGE_TYPE_TOAST, Message.bind(Message.codes.COPY_COMPLETE, { target: '回答項目' }))
    }

    /**
     * 回答項目貼り付け
     */
    const paste = () => {
      if (!store.getters['user/getAnswerDraftItems']) {
        addMessageQueue(MESSAGE_TYPE_ERROR, Message.codes.NONE_PASTE_TARGET)
        return
      }
      storeAnswerItems.splice(0)
      storeAnswerItems.push(...store.getters['user/getAnswerDraftItems'])
      updateAnswerItem(storeAnswerItems)
    }

    /**
     * 相手敬称の入力ボックスへの初期値設定
     */
    watch(
      () => proximityInfo,
      () => {
        if (proximityInfo.value == null || proximityInfo.value.proximity.answer_applicant_abbreviation == null) {
          form.answer_applicant_abbreviation = '貴■'
        } else {
          form.answer_applicant_abbreviation = proximityInfo.value.proximity.answer_applicant_abbreviation
        }
        if (proximityInfo.value?.proximity.answer_draft_title != null) {
          form.answer_draft_title = proximityInfo.value?.proximity.answer_draft_title
        }
      },
      { immediate: true, deep: true }
    )

    return {
      requestHeaderId,
      proximityInfo,
      form,
      answerDraftItem,
      answerRule,
      formRef,
      storeAnswerItems,
      updateAnswerItem,
      copy,
      paste,
    }
  },
})
</script>

<style lang="scss" scoped>
.el-collapse-item {
  ::v-deep(.el-collapse-item__content) {
    padding-bottom: 0px !important;
  }
  ::v-deep(.el-collapse-item__wrap) {
    border-top: none !important;
    border-bottom: none !important;
  }
  ::v-deep(.el-collapse-item__header) {
    border-top: none !important;
    border-bottom: none !important;
  }
}

.el-descriptions {
  ::v-deep(.el-descriptions__label) {
    width: 90px;
  }
}

.word-preview-area {
  margin-bottom: 50px;
}
</style>
